// Currency utility — single source of truth for INR vs USD across the app.
//
// Two distinct rules:
//
// 1. COACH UI (Business Dashboard, client packages, renewals, package history,
//    client cards). Currency = `coach.country === "IN" ? INR : USD`.
//
// 2. SUBSCRIPTION PRICING (the price the coach pays Seshly). Strict AND rule:
//    USD only if `coach.country !== "IN"` AND `Intl timezone !== "Asia/Kolkata"`.
//    Anything else → INR. The timezone check is the anti-VPN guardrail.
//
// Historical money stored on Firestore docs (e.g. `clients/{id}.amountPaid`)
// is locked to the currency at write time via the `currency` field on the doc.
// Reads should prefer that stamped value over the coach's *current* country.

import { isValidCountryCode } from "./countries";

// ── Static currency definitions ──────────────────────────────────────────
export const CURRENCIES = {
  INR: {
    code: "INR",
    symbol: "₹",
    locale: "en-IN",
    monthly: 599,
    yearly: 5999,
    authAmount: 2,             // refundable card-verification charge
    paymentMethods: ["upi", "card", "enach", "pnach"],
    planMonthly: "seshly_pro_monthly",
    planYearly: "seshly_pro_yearly",
  },
  USD: {
    code: "USD",
    symbol: "$",
    locale: "en-US",
    monthly: 12.99,
    yearly: 129,
    authAmount: 0.5,
    paymentMethods: ["card"],
    planMonthly: "seshly_pro_monthly_usd",
    planYearly: "seshly_pro_yearly_usd",
  },
  // GBP/EUR/AED: org (gym/studio) member-facing currency only. Never used
  // for Seshly's own subscription billing (getSubscriptionCurrency is
  // strictly INR/USD), so no Cashfree plan IDs needed here.
  GBP: { code: "GBP", symbol: "£", locale: "en-GB" },
  EUR: { code: "EUR", symbol: "€", locale: "en-IE" },
  AED: { code: "AED", symbol: "د.إ", locale: "ar-AE" },
};

// ── Org (gym/studio) member-facing currency ──────────────────────────────
// Distinct from the coach's own country-based currency and from Seshly's
// subscription-pricing currency. Set once by the org admin in Plans &
// Pricing (organizations/{orgId}/settings/pricing.currencyCode) and applies
// to every org-managed coach and client. Independent coaches are unaffected.
export function resolveOrgCurrency(currencyCode) {
  return CURRENCIES[currencyCode] || CURRENCIES.INR;
}

// ── Country / timezone helpers ───────────────────────────────────────────

export function isIndia(countryCode) {
  return countryCode === "IN";
}

export function timezoneIsIndia() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Kolkata";
  } catch {
    return true; // fail safe — assume India if we can't tell
  }
}

// ── Currency selection ───────────────────────────────────────────────────

/**
 * Coach-UI currency. Org-managed coaches (organizationId set) use their
 * org's chosen currency (passed in as `orgCurrency`, live from
 * AuthContext) — the admin sets this once for the whole studio.
 * Independent coaches keep the existing per-coach country-based rule.
 */
export function getCoachCurrency(coachProfile, orgCurrency) {
  if (coachProfile?.organizationId && orgCurrency) return orgCurrency;
  const code = coachProfile?.country;
  if (!isValidCountryCode(code)) return CURRENCIES.INR;
  return isIndia(code) ? CURRENCIES.INR : CURRENCIES.USD;
}

/**
 * Subscription-pricing currency. Strict AND rule — USD only if country
 * AND timezone both say non-India. Catches VPN / mis-set-country abuse.
 */
export function getSubscriptionCurrency(coachProfile) {
  const country = coachProfile?.country;
  const countryIsIndia = !isValidCountryCode(country) || isIndia(country);
  const tzIndia = timezoneIsIndia();
  if (countryIsIndia || tzIndia) return CURRENCIES.INR;
  return CURRENCIES.USD;
}

/**
 * Read the currency stamped on a money-bearing doc (client package, renewal,
 * etc.). Falls back to the coach's *current* currency if the doc predates
 * stamping. Always returns a CURRENCIES entry.
 */
export function getStampedCurrency(doc, coachProfile, orgCurrency) {
  const stamped = doc?.currency;
  if (stamped && CURRENCIES[stamped]) return CURRENCIES[stamped];
  return getCoachCurrency(coachProfile, orgCurrency);
}

// ── Formatting ───────────────────────────────────────────────────────────

/**
 * Format a number as currency. For USD we keep 2 decimals on the displayed
 * subscription price (12.99) but drop trailing zeros on package amounts
 * (clean `$200` instead of `$200.00`).
 */
export function formatMoney(amount, currency = CURRENCIES.INR, { decimals } = {}) {
  if (amount == null || Number.isNaN(Number(amount))) return `${currency.symbol}0`;
  const n = Number(amount);
  const showDecimals = decimals != null
    ? decimals
    : (currency.code === "USD" && n % 1 !== 0 ? 2 : 0);
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: "currency",
      currency: currency.code,
      minimumFractionDigits: showDecimals,
      maximumFractionDigits: showDecimals,
      currencyDisplay: "symbol",
    }).format(n);
  } catch {
    // Fallback if Intl is wonky
    return `${currency.symbol}${n.toLocaleString(currency.locale)}`;
  }
}

/** Convenience: just the symbol for inline use (e.g. an input prefix). */
export function currencySymbol(currency) {
  return (currency || CURRENCIES.INR).symbol;
}
